import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Newspaper, Star, Image } from 'lucide-react';
import Header from '../../components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import { newsAPI, testimonialsAPI } from '../../utils/api';

const AdminContent = () => {
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [newsDialogOpen, setNewsDialogOpen] = useState(false);
  const [testimonialDialogOpen, setTestimonialDialogOpen] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  
  const [newsForm, setNewsForm] = useState({
    title: '',
    content: '',
    summary: '',
    image_url: '',
    category: 'news',
    is_featured: false
  });
  
  const [testimonialForm, setTestimonialForm] = useState({
    name: '',
    designation: '',
    company: '',
    content: '',
    rating: 5,
    image_url: '',
    is_featured: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [newsRes, testimonialsRes] = await Promise.all([
        newsAPI.getAll({ include_inactive: true }),
        testimonialsAPI.getAll({ include_inactive: true })
      ]);
      setNews(newsRes.data);
      setTestimonials(testimonialsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // News handlers
  const handleNewsSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingNews) {
        await newsAPI.update(editingNews.id, newsForm);
        toast.success('News updated');
      } else {
        await newsAPI.create(newsForm);
        toast.success('News created');
      }
      setNewsDialogOpen(false);
      resetNewsForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEditNews = (item) => {
    setEditingNews(item);
    setNewsForm({
      title: item.title,
      content: item.content,
      summary: item.summary || '',
      image_url: item.image_url || '',
      category: item.category,
      is_featured: item.is_featured
    });
    setNewsDialogOpen(true);
  };

  const handleDeleteNews = async (id) => {
    if (window.confirm('Delete this news item?')) {
      try {
        await newsAPI.delete(id);
        toast.success('News deleted');
        loadData();
      } catch (error) {
        toast.error('Failed to delete');
      }
    }
  };

  const resetNewsForm = () => {
    setEditingNews(null);
    setNewsForm({
      title: '',
      content: '',
      summary: '',
      image_url: '',
      category: 'news',
      is_featured: false
    });
  };

  // Testimonial handlers
  const handleTestimonialSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTestimonial) {
        await testimonialsAPI.update(editingTestimonial.id, testimonialForm);
        toast.success('Testimonial updated');
      } else {
        await testimonialsAPI.create(testimonialForm);
        toast.success('Testimonial created');
      }
      setTestimonialDialogOpen(false);
      resetTestimonialForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEditTestimonial = (item) => {
    setEditingTestimonial(item);
    setTestimonialForm({
      name: item.name,
      designation: item.designation || '',
      company: item.company || '',
      content: item.content,
      rating: item.rating,
      image_url: item.image_url || '',
      is_featured: item.is_featured
    });
    setTestimonialDialogOpen(true);
  };

  const handleDeleteTestimonial = async (id) => {
    if (window.confirm('Delete this testimonial?')) {
      try {
        await testimonialsAPI.delete(id);
        toast.success('Testimonial deleted');
        loadData();
      } catch (error) {
        toast.error('Failed to delete');
      }
    }
  };

  const resetTestimonialForm = () => {
    setEditingTestimonial(null);
    setTestimonialForm({
      name: '',
      designation: '',
      company: '',
      content: '',
      rating: 5,
      image_url: '',
      is_featured: false
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-secondary">
      <Header />
      
      <div className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-primary mb-2">
                Content Management
              </h1>
              <p className="text-muted-foreground">
                Manage news, announcements, and testimonials
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/admin')}>
              Back to Dashboard
            </Button>
          </div>

          <Tabs defaultValue="news" className="space-y-6">
            <TabsList>
              <TabsTrigger value="news" className="flex items-center gap-2">
                <Newspaper className="h-4 w-4" /> News & Media
              </TabsTrigger>
              <TabsTrigger value="testimonials" className="flex items-center gap-2">
                <Star className="h-4 w-4" /> Testimonials
              </TabsTrigger>
            </TabsList>

            <TabsContent value="news">
              <div className="bg-white rounded-lg border shadow-sm p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">News & Announcements</h2>
                  <Button onClick={() => { resetNewsForm(); setNewsDialogOpen(true); }} data-testid="add-news-btn">
                    <Plus className="h-4 w-4 mr-2" /> Add News
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {news.map((item) => (
                    <div
                      key={item.id}
                      className={`border rounded-lg overflow-hidden ${!item.is_active ? 'opacity-50' : ''}`}
                    >
                      {item.image_url && (
                        <div className="h-40 bg-slate-100">
                          <img
                            src={item.image_url}
                            alt={item.title}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-xs px-2 py-1 rounded ${item.category === 'news' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                            {item.category}
                          </span>
                          {item.is_featured && (
                            <span className="text-xs px-2 py-1 rounded bg-accent/10 text-accent">Featured</span>
                          )}
                        </div>
                        <h3 className="font-semibold text-primary mb-2 line-clamp-2">{item.title}</h3>
                        <p className="text-sm text-muted-foreground line-clamp-2">{item.summary || item.content}</p>
                        <div className="flex justify-end gap-2 mt-4">
                          <Button size="sm" variant="ghost" onClick={() => handleEditNews(item)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteNews(item.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {news.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No news items yet. Add your first news article.
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="testimonials">
              <div className="bg-white rounded-lg border shadow-sm p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">Testimonials</h2>
                  <Button onClick={() => { resetTestimonialForm(); setTestimonialDialogOpen(true); }} data-testid="add-testimonial-btn">
                    <Plus className="h-4 w-4 mr-2" /> Add Testimonial
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {testimonials.map((item) => (
                    <div
                      key={item.id}
                      className={`border rounded-lg p-6 ${!item.is_active ? 'opacity-50' : ''}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          {item.image_url ? (
                            <img
                              src={item.image_url}
                              alt={item.name}
                              className="w-12 h-12 rounded-full object-cover"
                              onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(item.name)}&background=random`; }}
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                              <span className="text-accent font-semibold">{item.name.charAt(0)}</span>
                            </div>
                          )}
                          <div>
                            <h4 className="font-semibold">{item.name}</h4>
                            <p className="text-sm text-muted-foreground">
                              {item.designation}{item.company ? ` at ${item.company}` : ''}
                            </p>
                          </div>
                        </div>
                        {item.is_featured && (
                          <span className="text-xs px-2 py-1 rounded bg-accent/10 text-accent">Featured</span>
                        )}
                      </div>
                      
                      <div className="flex gap-1 mb-3">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-4 w-4 ${i < item.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`}
                          />
                        ))}
                      </div>
                      
                      <p className="text-sm text-muted-foreground italic">&ldquo;{item.content}&rdquo;</p>
                      
                      <div className="flex justify-end gap-2 mt-4">
                        <Button size="sm" variant="ghost" onClick={() => handleEditTestimonial(item)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteTestimonial(item.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {testimonials.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No testimonials yet. Add your first testimonial.
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* News Dialog */}
      <Dialog open={newsDialogOpen} onOpenChange={setNewsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingNews ? 'Edit News' : 'Add News'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleNewsSubmit} className="space-y-4">
            <div>
              <Label htmlFor="news-title">Title *</Label>
              <Input
                id="news-title"
                value={newsForm.title}
                onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="news-summary">Summary</Label>
              <Input
                id="news-summary"
                value={newsForm.summary}
                onChange={(e) => setNewsForm({ ...newsForm, summary: e.target.value })}
                placeholder="Brief summary for preview"
              />
            </div>
            
            <div>
              <Label htmlFor="news-content">Content *</Label>
              <Textarea
                id="news-content"
                value={newsForm.content}
                onChange={(e) => setNewsForm({ ...newsForm, content: e.target.value })}
                rows={5}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="news-image">Image URL</Label>
              <Input
                id="news-image"
                value={newsForm.image_url}
                onChange={(e) => setNewsForm({ ...newsForm, image_url: e.target.value })}
                placeholder="https://..."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select
                  value={newsForm.category}
                  onValueChange={(value) => setNewsForm({ ...newsForm, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="news">News</SelectItem>
                    <SelectItem value="announcement">Announcement</SelectItem>
                    <SelectItem value="update">Update</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center justify-between">
                <Label>Featured</Label>
                <Switch
                  checked={newsForm.is_featured}
                  onCheckedChange={(checked) => setNewsForm({ ...newsForm, is_featured: checked })}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setNewsDialogOpen(false)}>Cancel</Button>
              <Button type="submit">{editingNews ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Testimonial Dialog */}
      <Dialog open={testimonialDialogOpen} onOpenChange={setTestimonialDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTestimonial ? 'Edit Testimonial' : 'Add Testimonial'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTestimonialSubmit} className="space-y-4">
            <div>
              <Label htmlFor="testimonial-name">Name *</Label>
              <Input
                id="testimonial-name"
                value={testimonialForm.name}
                onChange={(e) => setTestimonialForm({ ...testimonialForm, name: e.target.value })}
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="testimonial-designation">Designation</Label>
                <Input
                  id="testimonial-designation"
                  value={testimonialForm.designation}
                  onChange={(e) => setTestimonialForm({ ...testimonialForm, designation: e.target.value })}
                  placeholder="e.g., CEO"
                />
              </div>
              
              <div>
                <Label htmlFor="testimonial-company">Company</Label>
                <Input
                  id="testimonial-company"
                  value={testimonialForm.company}
                  onChange={(e) => setTestimonialForm({ ...testimonialForm, company: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="testimonial-content">Testimonial *</Label>
              <Textarea
                id="testimonial-content"
                value={testimonialForm.content}
                onChange={(e) => setTestimonialForm({ ...testimonialForm, content: e.target.value })}
                rows={4}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="testimonial-image">Photo URL</Label>
              <Input
                id="testimonial-image"
                value={testimonialForm.image_url}
                onChange={(e) => setTestimonialForm({ ...testimonialForm, image_url: e.target.value })}
                placeholder="https://..."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Rating</Label>
                <Select
                  value={String(testimonialForm.rating)}
                  onValueChange={(value) => setTestimonialForm({ ...testimonialForm, rating: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 4, 3, 2, 1].map((r) => (
                      <SelectItem key={r} value={String(r)}>{r} Star{r > 1 ? 's' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center justify-between">
                <Label>Featured</Label>
                <Switch
                  checked={testimonialForm.is_featured}
                  onCheckedChange={(checked) => setTestimonialForm({ ...testimonialForm, is_featured: checked })}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTestimonialDialogOpen(false)}>Cancel</Button>
              <Button type="submit">{editingTestimonial ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminContent;
